import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MoveDown } from "lucide-react";
import KineticLines from "@/components/KineticLines";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const chapters = [
  {
    num: "01",
    title: "Real Job Sites",
    text: "Students step onto active residential construction projects and see how a home actually comes together — from foundation to finish.",
  },
  {
    num: "02",
    title: "Real Pros",
    text: "Builders and subcontractors walk students through the trades — framing, electrical, plumbing, finishing — and what a career in each one looks like.",
  },
  {
    num: "03",
    title: "Real Hands-On Work",
    text: "Whenever it's practical and safe, students pick up the tools and take part — not just watch from the sidelines.",
  },
];

const Cross = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} aria-hidden="true">
    <path d="M12 3v18M3 12h18" />
  </svg>
);

export default function Home() {
  const heroRef = useRef(null);
  const quoteRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const geoSlow = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const { scrollYProgress: quoteProgress } = useScroll({ target: quoteRef, offset: ["start end", "end start"] });
  const quoteShape = useTransform(quoteProgress, [0, 1], [40, -40]);

  return (
    <main data-testid="home-page">
      {/* HERO — typography over historic elevation linework */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-navy-ink" data-testid="hero-section">
        <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />

        {/* first BuilderEDU home — front elevation, white linework fading into the navy */}
        <motion.div
          style={{ y: geoSlow }}
          className="elevation-fade absolute inset-y-[4%] right-[-35%] sm:right-[-12%] lg:right-[-4%] w-[150%] sm:w-[90%] lg:w-[62%] pointer-events-none opacity-25 sm:opacity-40 lg:opacity-55"
          aria-hidden="true"
        >
          <img
            src="/assets/elevation-white.png"
            alt=""
            className="w-full h-full object-contain object-right"
            data-testid="hero-elevation"
          />
        </motion.div>

        {/* drafting registration marks */}
        <Cross className="absolute top-28 left-6 md:left-12 w-5 h-5 text-white/15" />
        <Cross className="absolute top-28 right-6 md:right-12 w-5 h-5 text-white/15" />
        <Cross className="absolute bottom-10 left-6 md:left-12 w-5 h-5 text-white/15" />
        <Cross className="absolute bottom-10 right-6 md:right-12 w-5 h-5 text-white/15" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-40 w-full">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="origin-left w-16 h-1.5 bg-brand mb-8"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand mb-8"
            data-testid="hero-overline"
          >
            A School District + Builder Partnership
          </motion.p>

          <h1 className="font-display uppercase text-white leading-[0.95] tracking-tight text-[13vw] sm:text-6xl md:text-7xl lg:text-8xl" data-testid="hero-headline">
            <KineticLines
              lines={["Building Skills.", "Building Careers.", "Building the Future."]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-white/75 text-base md:text-lg leading-relaxed"
            data-testid="hero-supporting-text"
          >
            BuilderEDU connects high school students with real residential construction projects,
            professional builders, and skilled tradespeople — with hands-on learning whenever possible.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/what-builderedu-does"
              data-testid="hero-what-button"
              className="group font-head font-bold uppercase tracking-[0.12em] text-sm bg-brand text-navy-ink px-8 py-4 inline-flex items-center gap-3 hover:-translate-y-1 hover:shadow-[0_16px_40px_-12px_rgba(245,166,35,0.5)] transition-all duration-300"
            >
              What is BuilderEDU?
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/connect"
              data-testid="hero-connect-button"
              className="font-head font-bold uppercase tracking-[0.12em] text-sm border border-white/40 text-white px-8 py-4 inline-flex items-center gap-3 hover:bg-white hover:text-navy-ink hover:-translate-y-1 transition-all duration-300"
            >
              Learn More / Stay Connected
            </Link>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-20 right-12 hidden lg:block font-head text-[10px] uppercase tracking-[0.3em] text-white/40"
          data-testid="hero-elevation-caption"
        >
          <span className="text-brand">Front Elevation B</span> — The First BuilderEDU Home
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-white/40"
        >
          <span className="font-head text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <MoveDown size={16} className="animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* INTRO STATEMENT */}
      <section className="bg-navy-ink text-white blueprint-grid py-24 md:py-32" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand mb-6">The Short Version</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-head font-semibold text-2xl md:text-4xl lg:text-[2.75rem] leading-snug max-w-4xl" data-testid="intro-statement">
              Most students learn about construction from a textbook.
              <span className="text-brand"> BuilderEDU students learn it on the job site</span> —
              seeing how homes are built and taking part whenever it's practical and safe.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NUMBERED CHAPTERS — typographic index */}
      <section className="bg-white py-24 md:py-32" data-testid="chapters-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between gap-8 mb-14 md:mb-20">
              <div>
                <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-4">How It Works</p>
                <h2 className="font-display uppercase text-navy text-4xl md:text-6xl leading-none" data-testid="chapters-heading">
                  The BuilderEDU<br />Experience
                </h2>
              </div>
              <span className="hidden md:block font-head text-xs uppercase tracking-[0.3em] text-navy/30 pb-2">03 Steps</span>
            </div>
          </Reveal>

          <div>
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div
                  className={`group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline border-t border-navy/10 py-12 md:py-16 ${i === 1 ? "md:pl-16" : i === 2 ? "md:pl-32" : ""}`}
                  data-testid={`chapter-${c.num}`}
                >
                  <span
                    className="md:col-span-3 font-display text-6xl md:text-8xl leading-none text-navy/10 group-hover:text-brand transition-colors duration-500"
                    data-testid={`chapter-${c.num}-number`}
                  >
                    {c.num}
                  </span>
                  <h3 className="md:col-span-4 font-display uppercase text-2xl md:text-4xl text-navy">
                    {c.title}
                  </h3>
                  <p className="md:col-span-5 text-slate-600 leading-relaxed max-w-md">
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-navy/10" />
          </div>
        </div>
      </section>

      {/* QUOTE BAND */}
      <section ref={quoteRef} className="relative bg-navy-ink text-white blueprint-grid py-28 md:py-40 overflow-hidden" data-testid="quote-band">
        <motion.div style={{ y: quoteShape }} className="absolute -top-10 right-[8%] w-40 h-40 border border-white/10 pointer-events-none" aria-hidden="true" />
        <motion.div style={{ y: quoteShape }} className="absolute bottom-8 left-[6%] w-20 h-20 bg-brand/90 pointer-events-none" aria-hidden="true" />
        <Cross className="absolute top-10 left-6 md:left-12 w-5 h-5 text-white/15" />
        <Cross className="absolute bottom-10 right-6 md:right-12 w-5 h-5 text-white/15" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl" data-testid="quote-band-text">
              The best classroom<br />has a <span className="text-brand">job site address.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand py-24 md:py-28" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <Reveal>
            <h2 className="font-display uppercase text-navy-ink text-4xl md:text-6xl leading-none" data-testid="home-cta-heading">
              Ready to see how<br />homes get built?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/connect"
              data-testid="home-cta-button"
              className="group font-head font-bold uppercase tracking-[0.12em] text-sm bg-navy-ink text-white px-8 py-4 inline-flex items-center gap-3 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            >
              Connect With BuilderEDU
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
